import React, { useEffect, useState } from 'react';

const STORAGE_KEY = 'proc-disc-coachmark-seen';

/**
 * ShrinkCoachmark — one-time tooltip that explains the disc's shrink
 * behaviour the first time a doctor types into the visit form. Without
 * this, the disc moving on its own reads as a bug.
 *
 * Stored under localStorage (not session) — show once per browser, not
 * once per shift.
 */
export default function ShrinkCoachmark({ size, palette }) {
  const [seen, setSeen] = useState(true); // assume seen until we check
  const [visible, setVisible] = useState(false);

  // Check storage on mount
  useEffect(() => {
    try {
      setSeen(window.localStorage?.getItem(STORAGE_KEY) === '1');
    } catch (_e) {
      setSeen(true); // private mode — don't pester
    }
  }, []);

  // Show the coachmark the first time the disc shrinks. Auto-dismiss
  // after 6s so it never blocks workflow.
  useEffect(() => {
    if (seen || size !== 'shrunk') return undefined;
    setVisible(true);
    const t = setTimeout(() => {
      setVisible(false);
      try { window.localStorage?.setItem(STORAGE_KEY, '1'); } catch (_e) { /* ignore */ }
      setSeen(true);
    }, 6000);
    return () => clearTimeout(t);
  }, [size, seen]);

  const dismiss = () => {
    setVisible(false);
    try { window.localStorage?.setItem(STORAGE_KEY, '1'); } catch (_e) { /* ignore */ }
    setSeen(true);
  };

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'absolute',
        right: '5rem',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 50,
        background: palette.bg,
        color: palette.text,
        border: `1px solid ${palette.border}`,
        borderRadius: 10,
        padding: '0.625rem 0.75rem',
        fontSize: '0.6875rem',
        maxWidth: 220,
        boxShadow: '0 6px 20px rgba(15,23,42,0.15)',
      }}
    >
      <strong style={{ display: 'block', marginBottom: 2 }}>
        Procedures auto-shrinks
      </strong>
      <span style={{ opacity: 0.85 }}>
        It makes room when you type. Click the disc to bring it back.
      </span>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss tip"
        style={{
          position: 'absolute',
          top: 4,
          right: 6,
          background: 'transparent',
          border: 'none',
          color: palette.muted,
          cursor: 'pointer',
          fontSize: '0.75rem',
        }}
      >
        ✕
      </button>
    </div>
  );
}
